/**
 * ProcessingPanel: the processing queue's controls for staff (admin only:
 * the Rigs console's Processing tab, beside Review). A small stand-in for the
 * monitoring screen that comes later (event pipeline design 13, section 8),
 * in the console's own look (the Review tab's chips, banners and dialogs).
 *
 * It shows whether processing is paused (the queue starts paused), the jobs
 * switched on for processing ("process only these jobs"), this job's
 * captures in the queue by state, the processing computers, and each of this
 * job's tasks. It acts only through the queue's staff callables
 * (rigv2Callables.js; docs/ingest/17-processing-queue-contract.md section 8):
 * pause or resume, switch this job on or off, and retry, cancel, hold or
 * release one task. Slice 3.2 adds starting work (StartWork.jsx, startWork:
 * admin or rig_operator): the "on" picker (automatic, or a computer that can
 * do the work), "Process" on each queued capture and on the whole job,
 * usable from a phone. The queue carries no names; the names shown come from
 * this job's capture records, which admins read already (the Review tab).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./rigv2-review.css";
import "./rigv2-processing.css";
import { useFirestoreCollection } from "../../../../lib/useFirestore.js";
import { rigv2 } from "./rigv2Callables.js";
import { useModalDialog } from "./dialogFocus.js";
import { snOf } from "./deliveryReviewModel.js";
import { ProcessRowButton, StartWorkCard, useStartWork } from "./StartWork.jsx";

// The queue is read every 30 seconds while the tab is on screen (one job's tasks: a few hundred reads).
const POLL_MS = 30_000;
// A processing computer is online when it sent a heartbeat in the last 3 minutes (the Machines page's rule).
const ONLINE_WINDOW_MS = 3 * 60 * 1000;
export const PAGE_SIZE = 50;

/* ── the rules the screen follows (pure: exported for the test) ── */

export const STATE_LABEL = {
  needs_staff: "Needs you", failed: "Failed", claimed: "Processing", queued: "In the queue",
  held: "Waiting or held", done: "Done", cancelled: "Cancelled",
};
/** One task's word: a held task is "Held" when staff held it, else "Waiting" (for the job list or the group recipe). */
export const stateLabelOf = (row) =>
  (row.state === "held" ? (row.reasonCode === "staff_hold" ? "Held" : "Waiting") : STATE_LABEL[row.state] ?? row.state);

/** What an action did, in words (the row's own line says why a task waits or needs a person). */
export function resultText(action, state) {
  if (state === "queued") return action === "retry" ? "Retried: back in the queue." : "Back in the queue.";
  if (state === "cancelled") return action === "cancel" ? "Cancelled." : "Cancelled: see why above.";
  if (state === "held") return action === "hold" ? "Held." : "Waiting: see why above.";
  if (state === "needs_staff") return "Needs you: see why above.";
  return "Done.";
}
const STATE_CHIP = {
  needs_staff: "needs", failed: "error", claimed: "working", queued: "working", held: "paused", done: "live", cancelled: "stopped",
};
/** The order of the counts and of the list: what needs Ivin first. */
export const STATE_ORDER = ["needs_staff", "failed", "claimed", "queued", "held", "done", "cancelled"];

/**
 * What one task may do now, by the queue's own rules (functions
 * services/processingTask.ts: RETRYABLE_STATES, CANCELLABLE, HOLDABLE; and
 * releaseProcessingTask, which releases a staff hold). A hold that a switch
 * keeps (the job list, the group recipe) is released by that switch, not here.
 */
export function taskActionsOf(row) {
  const s = row?.state;
  return {
    // A capture whose guest delivery closed is never processed again: the server refuses its retry.
    retry: s === "needs_staff" || s === "failed" || (s === "cancelled" && row.reasonCode !== "delivery_closed" && row.reasonCode !== "version_deleting"),
    release: s === "held" && row.reasonCode === "staff_hold",
    hold: s === "queued" || s === "claimed" || (s === "held" && row.reasonCode !== "staff_hold"),
    cancel: s === "queued" || s === "claimed" || s === "held" || s === "needs_staff" || s === "failed",
  };
}

/**
 * A job's guest delivery switches as guest delivery reads them (the review on
 * unless delivery.requireReview is false; auto-send on only when
 * delivery.autoSend is true), from its document.
 */
export const deliverySwitchesOf = (job) => {
  const d = job?.delivery && typeof job.delivery === "object" ? job.delivery : {};
  return { reviewOn: d.requireReview !== false, autoSendOn: d.autoSend === true };
};

/**
 * What is wrong with a job's delivery switches for processing, or "": a job
 * is processed only while guest delivery holds every model for Ivin's
 * approval (the one rule, functions processingTask.ts automationProblemOf).
 * Auto-send may be on (Ivin, 2026-10-01): it emails only approved models.
 * The server refuses to switch such a job on (setProcessingJobAllowed).
 */
export function switchesProblem({ reviewOn, found = true }) {
  if (!found) return "its job document is gone";
  if (!reviewOn) return "its review is off (delivery.requireReview): a reported model goes online without your approval";
  return "";
}

/**
 * The same, for a job already switched on (its review field turned off since,
 * by hand): what is off, without saying a model goes out, because while the
 * job is switched on guest delivery holds every model for approval whatever
 * that field says (services/delivery.ts, the one rule).
 */
export function listedFieldProblem({ reviewOn, found = true }) {
  if (!found) return "its job document is gone";
  if (!reviewOn) return "its review switch is off (delivery.requireReview)";
  return "";
}
const HELD_ANYWAY = "while it is switched on, the Suite still holds every model for your approval; once it is switched off, that switch counts";

/** Why this job cannot be switched on, or "" (the server refuses the same). */
export function jobOnProblem(job) {
  const p = switchesProblem(deliverySwitchesOf(job));
  if (!p) return "";
  return `This job cannot be switched on: ${p}. Change that first.`;
}

/** The jobs switched on whose delivery switches are not safe now (the server's listedJobs). */
export const unsafeListedJobs = (listedJobs) =>
  (listedJobs ?? []).map((j) => ({ ...j, problem: listedFieldProblem(j) })).filter((j) => j.problem);

/** The people on a capture record: the lead, and how many more (the Review tab's order). */
export function captureTitle(a) {
  if (!a) return "";
  const list = Array.isArray(a.members) && a.members.length ? a.members : [a.subjectSnapshot].filter(Boolean);
  const names = list.map((m) => String(m?.displayNameAtFire || m?.subjectName || "").trim()).filter(Boolean);
  if (!names.length) return "";
  return names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
}

const shortTime = (ms) => (ms ? new Date(ms).toLocaleString("en-US", {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/New_York",
}) : "");

/** The line under a task: where it is, or why it waits. */
export function taskDetail(row, machineLabel) {
  if (row.state === "claimed") {
    const pct = typeof row.progress === "number" ? `${Math.round(row.progress)}%` : "";
    return [machineLabel || "A processing computer", row.stage, pct].filter(Boolean).join(" · ");
  }
  if (row.state === "done") return `Model reported ${shortTime(row.doneAt)}: see it on the Review tab.`;
  return row.reason || "";
}

/**
 * Slice 3.1: the machine a queued task is assigned to, while the assignment
 * lasts (the scheduler's 90-second hint, or a person's "run on this
 * computer"); "" otherwise. A hint, not a lease: another computer may take
 * the task once it ends.
 */
export function assignedLine(row, machineLabel, now = Date.now()) {
  const a = row.assigned;
  if (row.state !== "queued" || !a || !(a.until > now)) return "";
  const who = machineLabel || "a processing computer";
  return a.by === "person" ?
    `Chosen to run on ${who} (until ${shortTime(a.until)})` :
    `Next for ${who}: the best-suited free computer`;
}

/** Slice 3.1: what kind of work a task is ("" for processing a capture, the queue's first kind). */
export const kindLabel = (row) => (row.kind && row.kind !== "process" ? `${String(row.kind).replace("_", " ")} task` : "");

/** The last failure, while it still matters (the task is back in the queue, or needs a person). */
export function lastErrorLine(row) {
  const e = row.lastError;
  if (!e || !(row.state === "queued" || row.state === "needs_staff" || row.state === "failed")) return "";
  const what = e.errorClass === "lost" ? "the processing computer stopped answering" : `${e.errorClass} error`;
  return `Last try: ${what}${e.message && e.errorClass !== "lost" ? `, ${e.message}` : ""}.`;
}

export const sortTasks = (rows) => [...rows].sort((x, y) =>
  (STATE_ORDER.indexOf(x.state) - STATE_ORDER.indexOf(y.state)) || (x.createdAt - y.createdAt) || x.taskId.localeCompare(y.taskId));

/** The processing computers: paired with AutoCycle, not revoked, with what the queue knows of each. */
export function computersOf(machines, reservations, now) {
  const byUid = Object.fromEntries((reservations ?? []).map((r) => [r.uid, r]));
  return (machines ?? [])
    .filter((m) => !m.revoked && Array.isArray(m.capabilities) && m.capabilities.includes("autocycle"))
    .map((m) => {
      const r = byUid[m.id] ?? null;
      return {
        uid: m.id,
        label: m.label || m.id,
        online: now - (m.lastSeenAt || 0) < ONLINE_WINDOW_MS,
        activeTaskId: r?.activeTaskId ?? null,
        cooldownUntil: r?.cooldownUntil ?? null,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

const computerLine = (c) => {
  const parts = [c.label, c.online ? "online" : "offline"];
  if (c.activeTaskId) parts.push(`working on …${c.activeTaskId.slice(-4)}`);
  else if (c.online) parts.push("idle");
  if (c.cooldownUntil) parts.push(`resting after three faults until ${shortTime(c.cooldownUntil)}`);
  return parts.join(" · ");
};

/* ── small pieces ── */

function StatusChip({ kind, children }) {
  return <span className={`rv-status ${kind}`}><i aria-hidden="true" />{children}</span>;
}

// The console's modal dialog (useModalDialog): Escape or the scrim closes it.
function Dialog({ label, onClose, children }) {
  const frameRef = useRef(null);
  useModalDialog(frameRef);
  return (
    <div className="rigv2-modal-scrim rv-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="rv-dialog" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={frameRef}
        onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
        {children}
      </div>
    </div>
  );
}

function ConfirmDialog({ title, lines, confirmLabel, danger = false, withNote = false, busy = false, onConfirm, onClose }) {
  const [note, setNote] = useState("");
  return (
    <Dialog label={title} onClose={onClose}>
      <p className="rv-dialog-title">{title}</p>
      <div className="rv-dialog-lines">{lines.filter(Boolean).map((l) => <span key={l}>{l}</span>)}</div>
      {withNote && (
        <label className="rv-field">
          <span className="rv-label">Note (optional)</span>
          <input className="rv-input" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
        </label>
      )}
      <div className="rv-dialog-actions">
        <button type="button" className="rigv2-btn" onClick={onClose}>Back</button>
        <button type="button" className={`rigv2-btn ${danger ? "rv-danger" : "primary rv-primary"}`} disabled={busy}
          onClick={() => onConfirm(note.trim())}>{busy ? "Saving…" : confirmLabel}</button>
      </div>
    </Dialog>
  );
}

function JobLabel({ entry, currentId }) {
  const problem = listedFieldProblem(entry);
  return (
    <span className="pp-job">
      {entry.name ? `${entry.name} ` : ""}<span className="mono rv-muted">{entry.jobId}</span>{entry.jobId === currentId ? " (this job)" : ""}
      {problem && <span className="rv-line warn"> (careful: {problem}; held for your approval anyway while switched on)</span>}
    </span>
  );
}

/* ── the view (pure props: exported for the test) ── */

export function ProcessingPanelView({
  jobId, job, view, captures, machines, now, busy = null, notes = {}, filter = "all", limit = PAGE_SIZE,
  onPause, onResume, onJob, onTask, onFilter, onMore, start = null, onAskJob,
}) {
  const paused = view.switches?.paused !== false;
  const allowedJobs = Array.isArray(view.switches?.allowedJobs) ? view.switches.allowedJobs : [];
  const listed = allowedJobs.includes(jobId);
  // Each job switched on, as its document says now (the server's listedJobs); this job's own from its document here.
  const listedJobs = allowedJobs.map((id) => (view.listedJobs ?? []).find((j) => j.jobId === id)
    ?? (id === jobId ? { jobId: id, found: true, name: job?.displayName || job?.clientName || "", ...deliverySwitchesOf(job) }
      : { jobId: id, found: true, name: "", reviewOn: true, autoSendOn: false }));
  const problem = jobOnProblem(job);
  const listedProblem = listed ? listedFieldProblem(deliverySwitchesOf(job)) : "";
  const counts = view.counts ?? {};
  const rows = useMemo(() => sortTasks(view.rows ?? []), [view.rows]);
  const titles = useMemo(() => Object.fromEntries((captures ?? []).map((a) => [snOf(a), captureTitle(a)])), [captures]);
  const computers = computersOf(machines, view.machines, now);
  const labelOf = (uid) => computers.find((c) => c.uid === uid)?.label || (machines ?? []).find((m) => m.id === uid)?.label || "";
  const total = STATE_ORDER.reduce((n, s) => n + (counts[s] ?? 0), 0);
  const filtered = filter === "all" ? rows : rows.filter((r) => r.state === filter);
  const shown = filtered.slice(0, limit);
  const acting = Boolean(busy);

  return (
    <>
      <div className="pp-switches">
        <div className="pp-switch">
          <div className="pp-switch-main">
            <span className="rv-label">Processing</span>
            <StatusChip kind={paused ? "paused" : "live"}>{paused ? "Paused" : "On"}</StatusChip>
          </div>
          <span className="rv-muted">
            {paused
              ? "No processing computer takes a new capture. One already working finishes it."
              : "Processing computers take the next capture of a job switched on, one capture each."}
          </span>
          <button type="button" className={`rigv2-btn small ${paused ? "primary rv-primary" : ""}`} disabled={acting}
            onClick={paused ? onResume : onPause}>
            {busy === "pause" ? "Saving…" : paused ? "Resume processing" : "Pause processing"}
          </button>
        </div>
        <div className={`pp-switch ${problem ? "warn" : ""}`}>
          <div className="pp-switch-main">
            <span className="rv-label">This job</span>
            <StatusChip kind={listed ? "live" : "stopped"}>{listed ? "Switched on" : "Switched off"}</StatusChip>
          </div>
          <span className="rv-muted">
            {listed
              ? "Its captures go to the processing computers while processing is on."
              : "Its captures are kept here, waiting, until you switch it on."}
          </span>
          {!listed && problem && <span className="rv-line warn">{problem}</span>}
          {listedProblem && (
            <span className="rv-line warn">
              Careful: this job is switched on, but {listedProblem}: {HELD_ANYWAY}. Change it back (Settings tab, Automation).
            </span>
          )}
          <button type="button" className="rigv2-btn small" disabled={acting || (!listed && Boolean(problem))}
            onClick={() => onJob(!listed)}>
            {busy === "job" ? "Saving…" : listed ? "Switch this job off" : "Switch this job on"}
          </button>
        </div>
        {start && <StartWorkCard start={start} jobListed={listed} paused={paused} onAskJob={onAskJob} />}
      </div>

      <div className="pp-facts">
        <span>
          <span className="k">Jobs switched on:</span>
          {listedJobs.length
            ? listedJobs.map((entry, i) => <React.Fragment key={entry.jobId}>{i ? ", " : ""}<JobLabel entry={entry} currentId={jobId} /></React.Fragment>)
            : "none"}
        </span>
        <span>
          <span className="k">Processing computers:</span>
          {computers.length
            ? computers.map((c, i) => <React.Fragment key={c.uid}>{i ? "; " : ""}{computerLine(c)}</React.Fragment>)
            : "none paired (Traffic, Machines: pair one with AutoCycle)"}
        </span>
        <span className="rv-muted">
          A model waits for your approval on the Review tab before any guest sees it: a job is switched on only while its
          review is on, and while it is switched on the Suite holds every model for approval anyway. With its auto-send on,
          an approved model is emailed at once; otherwise when you press Send. To stop one capture now, Hold or Cancel it here (never Discard, which deletes its photos).
        </span>
      </div>

      <div className="rv-toolbar" role="group" aria-label="This job's captures in the queue">
        {[["all", "All", total], ...STATE_ORDER.map((s) => [s, STATE_LABEL[s], counts[s] ?? 0])].map(([id, label, n]) => (
          <button key={id} type="button" className={`rigv2-chip ${filter === id ? "on" : ""}`} aria-pressed={filter === id}
            onClick={() => onFilter(id)}>
            {label} {n}
          </button>
        ))}
      </div>
      {view.truncated && <p className="rv-footnote">This job has more tasks than the queue lists at once: the counts and rows are its first 2000.</p>}

      {rows.length === 0 ? (
        <div className="rv-empty">
          <p className="rv-empty-title">No captures in the queue yet</p>
          <p>A capture joins the queue once its photos are verified in the cloud.</p>
        </div>
      ) : shown.length === 0 ? (
        <div className="rigv2-empty">None here.</div>
      ) : (
        <ul className="pp-rows">
          {shown.map((row) => {
            const acts = taskActionsOf(row);
            const title = titles[row.sn] || "";
            const note = notes[row.taskId] || start?.notes?.[row.taskId];
            const mine = busy === `task:${row.taskId}`;
            const attention = row.state === "needs_staff" || row.state === "failed";
            const detail = taskDetail(row, labelOf(row.machineUid));
            const last = lastErrorLine(row);
            const assigned = assignedLine(row, row.assigned ? labelOf(row.assigned.machineUid) : "");
            return (
              <li key={row.taskId} className={`pp-row ${attention ? "warn" : ""}`}>
                <div className="pp-row-main">
                  <span className="rv-name">{title || "Capture"}</span>
                  <span className="rv-sn mono" title={row.sn}>{row.sn}</span>
                  <StatusChip kind={STATE_CHIP[row.state] ?? "stopped"}>{stateLabelOf(row)}</StatusChip>
                </div>
                <span className="rv-meta">
                  {[
                    kindLabel(row),
                    row.recipe === "group" ? `group of ${row.memberCount}` : "",
                    typeof row.photoCount === "number" ? `${row.photoCount} photos` : "",
                    row.attemptCount > 0 ? `try ${row.attemptCount} of ${row.attemptLimit}` : "",
                    `in the queue since ${shortTime(row.createdAt)}`,
                  ].filter(Boolean).join(" · ")}
                </span>
                {detail && <span className={`rv-line ${attention ? "warn" : ""}`}>{detail}</span>}
                {assigned && <span className="rv-line muted">{assigned}</span>}
                {last && <span className="rv-line muted">{last}</span>}
                {(acts.retry || acts.release || acts.hold || acts.cancel || (start && row.state === "queued")) && (
                  <div className="rv-actions">
                    {start && <ProcessRowButton start={start} row={row} />}
                    {acts.retry && <button type="button" className="rigv2-btn small" disabled={acting} onClick={() => onTask(row, "retry")}>{mine ? "Saving…" : "Retry"}</button>}
                    {acts.release && <button type="button" className="rigv2-btn small" disabled={acting} onClick={() => onTask(row, "release")}>Release</button>}
                    {acts.hold && <button type="button" className="rigv2-btn small" disabled={acting} onClick={() => onTask(row, "hold")}>Hold</button>}
                    {acts.cancel && <button type="button" className="rigv2-btn small rv-danger" disabled={acting} onClick={() => onTask(row, "cancel")}>Cancel</button>}
                  </div>
                )}
                {note && <span className={note.kind === "error" ? "rv-note" : "pp-ok"} role="status">{note.text}</span>}
              </li>
            );
          })}
        </ul>
      )}
      {filtered.length > shown.length && (
        <p className="rv-footnote">
          Showing {shown.length} of {filtered.length}.
          <button type="button" className="rv-link" onClick={onMore}>Show {Math.min(PAGE_SIZE, filtered.length - shown.length)} more</button>
        </p>
      )}
    </>
  );
}

/* ── the tab ── */

const CALL = {
  retry: (p) => rigv2.retryProcessingTask(p),
  release: (p) => rigv2.releaseProcessingTask(p),
  hold: (p) => rigv2.holdProcessingTask(p),
  cancel: (p) => rigv2.cancelProcessingTask(p),
};

export default function ProcessingPanel({ jobId, job }) {
  const { data: captures } = useFirestoreCollection(jobId ? `jobs/${jobId}/captureAttempts` : null);
  const { data: machines } = useFirestoreCollection("machines");
  const [view, setView] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(null);
  const [banner, setBanner] = useState(null);
  const [notes, setNotes] = useState({});
  const [dialog, setDialog] = useState(null);
  const [filter, setFilter] = useState("all");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [now, setNow] = useState(() => Date.now());
  const live = useRef(true);
  const seq = useRef(0);
  const loadRef = useRef(null);
  // Slice 3.2: the "on" picker and the Process buttons (StartWork.jsx); the queue is read again after each press.
  const start = useStartWork(jobId, {
    onDone: () => loadRef.current?.(),
    onRowPress: (taskId) => setNotes((n) => ({ ...n, [taskId]: null })),
  });
  const reloadOptions = start.reload;

  // The queue as the server sees it: now, every 30 seconds while the tab is on screen, and after each action.
  // A late answer never overwrites a newer one.
  const load = useCallback(async () => {
    if (!jobId) return;
    const mine = ++seq.current;
    try {
      const v = await rigv2.getProcessingQueue({ jobId });
      if (!live.current || mine !== seq.current) return;
      setView(v);
      setLoadError("");
      setNow(Date.now());
    } catch (e) {
      if (!live.current || mine !== seq.current) return;
      setLoadError(`Could not load the processing queue (${e?.message || "no answer"}). It tries again in 30 seconds.`);
    }
  }, [jobId]);
  useEffect(() => { loadRef.current = load; }, [load]);
  useEffect(() => {
    live.current = true;
    setView(null);
    setNotes({});
    load();
    const hidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";
    const t = setInterval(() => { if (!hidden()) { load(); reloadOptions(); } }, POLL_MS);
    const onVisible = () => { if (!hidden()) { load(); reloadOptions(); } };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      live.current = false;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load, reloadOptions]);

  const act = async (key, fn, done) => {
    setBusy(key);
    setBanner(null);
    try {
      const r = await fn();
      setBanner({ kind: "info", text: done(r) });
      return true;
    } catch (e) {
      setBanner({ kind: "error", text: e?.message || "That did not work. Try again." });
      return false;
    } finally {
      setBusy(null);
      load();
    }
  };

  const pause = () => act("pause", () => rigv2.setProcessingPaused({ paused: true }),
    () => "Processing paused: no computer takes a new capture. One already working finishes it.");
  const resume = async () => {
    setDialog(null);
    await act("pause", () => rigv2.setProcessingPaused({ paused: false }), () => "Processing is on.");
  };
  const groupReady = view?.switches?.groupRecipeReady === true;
  const setJob = async (allowed) => {
    setDialog(null);
    await act("job", () => rigv2.setProcessingJobAllowed({ jobId, allowed }), (r) => (allowed
      ? `This job is switched on: ${r?.moved ?? 0} ${r?.moved === 1 ? "capture" : "captures"} no longer waiting for it (into the queue${groupReady ? "" : "; a group capture waits for the group recipe"}).`
      : `This job is switched off: ${r?.moved ?? 0} ${r?.moved === 1 ? "capture" : "captures"} back to waiting. A capture being processed finishes.`));
  };
  const task = async (row, action, note = "") => {
    setDialog(null);
    setBusy(`task:${row.taskId}`);
    setNotes((n) => ({ ...n, [row.taskId]: null }));
    start.clearNote(row.taskId);
    try {
      const r = await CALL[action]({ taskId: row.taskId, ...(note ? { note } : {}) });
      setNotes((n) => ({ ...n, [row.taskId]: { kind: "ok", text: resultText(action, r?.state) } }));
    } catch (e) {
      setNotes((n) => ({ ...n, [row.taskId]: { kind: "error", text: e?.message || "That did not work. Try again." } }));
    } finally {
      setBusy(null);
      load();
    }
  };
  // Cancel and Hold ask first (with an optional note); Retry and Release go at once.
  const onTask = (row, action) => {
    if (action === "cancel" || action === "hold") setDialog({ kind: action, row });
    else task(row, action);
  };
  const listedCount = (view?.switches?.allowedJobs ?? []).length;
  const unsafe = unsafeListedJobs(view?.listedJobs);
  // This job's captures that wait for the job list (the rows the queue lists: up to 2000).
  const waitingForList = (view?.rows ?? []).filter((r) => r.state === "held" && r.reasonCode === "job_not_listed").length;

  return (
    <div className="rigv2-review rigv2-processing">
      <div className="rv-head">
        <div className="rv-head-left">
          <span className="rv-eyebrow">Processing</span>
          <span className="rv-sub">The processing computers make each capture&apos;s 3D model. This job&apos;s captures, and the controls.</span>
        </div>
        <button type="button" className="rigv2-btn small" disabled={Boolean(busy)} onClick={load}>Refresh</button>
      </div>

      {banner && (
        <div className={`rv-banner ${banner.kind}`} role="status">{banner.text}
          <button type="button" className="rv-link" onClick={() => setBanner(null)}>Dismiss</button>
        </div>
      )}
      {loadError && <div className="rv-banner error" role="status">{loadError}</div>}
      {start.banner && (
        <div className={`rv-banner ${start.banner.kind}`} role="status">{start.banner.text}
          <button type="button" className="rv-link" onClick={start.dismissBanner}>Dismiss</button>
        </div>
      )}

      {!view ? (
        loadError ? null : <div className="rigv2-empty">Loading the processing queue…</div>
      ) : (
        <ProcessingPanelView
          jobId={jobId} job={job} view={view} captures={captures} machines={machines} now={now}
          busy={busy} notes={notes} filter={filter} limit={limit}
          onPause={pause}
          onResume={() => setDialog({ kind: "resume" })}
          onJob={(on) => (on ? setDialog({ kind: "jobOn" }) : setJob(false))}
          onTask={onTask}
          onFilter={(id) => { setFilter(id); setLimit(PAGE_SIZE); }}
          onMore={() => setLimit((n) => n + PAGE_SIZE)}
          start={start}
          onAskJob={() => (start.target ? setDialog({ kind: "startJob" }) : start.startJob())}
        />
      )}

      {dialog?.kind === "resume" && (
        <ConfirmDialog
          title="Resume processing?"
          lines={[
            listedCount
              ? `Processing computers start taking the captures of the ${listedCount === 1 ? "job" : `${listedCount} jobs`} switched on (listed on this tab).`
              : "No job is switched on yet: nothing is processed until you switch one on.",
            ...unsafe.map((j) => `Careful: ${j.name || j.jobId} is switched on, but ${j.problem} (held for your approval anyway while it is switched on).`),
            listedCount && !unsafe.length
              ? "Each of them holds every model for your approval on the Review tab (with auto-send on, an approved model is emailed at once)."
              : "",
          ]}
          confirmLabel="Resume processing" busy={busy === "pause"} onConfirm={resume} onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "jobOn" && (
        <ConfirmDialog
          title="Switch this job on for processing?"
          lines={[
            `Its captures waiting for this switch go into the queue: ${waitingForList} now, and every new one as its photos are verified.${groupReady ? "" : " A group capture then waits for the group recipe."}`,
            view?.switches?.paused !== false
              ? "Processing is paused: nothing is processed until you resume it."
              : "Processing is on: the processing computers start on the queued ones within a minute.",
            "Each model waits for your approval on the Review tab before any guest sees it.",
          ]}
          confirmLabel="Switch on" busy={busy === "job"} onConfirm={() => setJob(true)} onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "startJob" && (
        <ConfirmDialog
          title={`Process this job's captures on ${start.labelOf(start.target)}?`}
          lines={[
            `Up to 20 of this job's captures in the queue (the oldest first; fewer if some are already held for a computer) are held for ${start.labelOf(start.target)} for 10 minutes; it takes them one after another. The rest stay in the queue for any free computer; press again once some are done to hold the next ones.`,
            "Captures whose photos are in the cloud but not in the queue yet join it now.",
            view?.switches?.paused !== false ? "Processing is paused: nothing starts until you resume it." : "",
          ]}
          confirmLabel="Process on it" busy={start.busy === "startJob"}
          onConfirm={() => { setDialog(null); start.startJob(); }} onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "cancel" && (
        <ConfirmDialog
          title={`Cancel processing of ${dialog.row.sn}?`}
          lines={[
            dialog.row.state === "claimed" ? "The processing computer working on it stops within a minute and deletes its copy." : "",
            "The capture's photos stay in the cloud. Retry puts it back in the queue.",
          ]}
          confirmLabel="Cancel processing" danger withNote busy={busy === `task:${dialog.row.taskId}`}
          onConfirm={(note) => task(dialog.row, "cancel", note)} onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "hold" && (
        <ConfirmDialog
          title={`Hold ${dialog.row.sn}?`}
          lines={[
            dialog.row.state === "claimed" ? "The processing computer working on it stops within a minute and deletes its copy." : "",
            "It stays out of the queue, whatever the switches say, until you release it here.",
          ]}
          confirmLabel="Hold" withNote busy={busy === `task:${dialog.row.taskId}`}
          onConfirm={(note) => task(dialog.row, "hold", note)} onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
